import React, { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';

import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';
import type { UserUpdateInfoData } from '@confluence/approvals/entry-points/ApprovalsContext.ts';

import { useContentStateInfoPopupCardPlacement } from './ContentStateInfoPopupCardPlacementContext.tsx';

const i18n = defineMessages({
	openProfileCard: {
		id: 'content-state.contentStateInfoPopup.open-profile-card.non-final',
		defaultMessage: 'Open profile card for {name}',
		description: 'Accessible name for the trigger that opens a user profile card.',
	},
});

export type UserUpdateInfoProps = UserUpdateInfoData;

/**
 * Displays user avatar and name for the content state update metadata.
 * Wraps with ProfileCardWrapper to show a profile card using click activation and
 * popup-relative placement when a card placement context is available.
 * Handles spacing and padding around the avatar.
 *
 * @param displayName - User's display name (required)
 * @param avatarUrl - Optional URL of user's avatar image
 * @param userId - Confluence account ID for the profile-card integration (required)
 */
export const UserUpdateInfo: FC<UserUpdateInfoProps> = ({ avatarUrl, displayName, userId }) => {
	const intl = useIntl();
	const cardPlacement = useContentStateInfoPopupCardPlacement();

	if (cardPlacement) {
		return (
			<ProfileCardWrapper
				key={userId}
				userId={userId}
				trigger="click"
				fullName={displayName}
				ariaLabel={intl.formatMessage(i18n.openProfileCard, { name: displayName })}
				position={cardPlacement.position}
				offset={cardPlacement.offset}
			>
				<Inline space="space.050" alignBlock="center" ref={cardPlacement.setTriggerElement}>
					<Avatar src={avatarUrl} name={displayName} size="xxsmall" />
					<Text size="small">{displayName}</Text>
				</Inline>
			</ProfileCardWrapper>
		);
	}

	return (
		<ProfileCardWrapper userId={userId}>
			<Inline space="space.050" alignBlock="center">
				<Avatar src={avatarUrl} name={displayName} size="xxsmall" />
				<Text size="small">{displayName}</Text>
			</Inline>
		</ProfileCardWrapper>
	);
};
